import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

const C = {
  bg: "#0a0a0f", card: "#13131a", border: "#2a2a3a",
  accent: "#c9a84c", text: "#f0f0f0", muted: "#888",
  error: "#ff6b6b", success: "#4caf50",
};

export default function LoginPage() {
  const navigate = useNavigate();

  // steps: phone → pin | otp → (if otp+existing) set-pin → dashboard
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState(["", "", "", ""]);
  const [newPin, setNewPin] = useState(["", "", "", ""]);
  const [confirmPin, setConfirmPin] = useState(["", "", "", ""]);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);

  const [isExistingUser, setIsExistingUser] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [idTokenForPin, setIdTokenForPin] = useState(null); // saved after OTP verify

  const [loading, setLoading] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [confirmResult, setConfirmResult] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const recaptchaVerifierRef = useRef(null);
  const otpRefs = useRef([]);
  const pinRefs = useRef([]);
  const newPinRefs = useRef([]);
  const confirmPinRefs = useRef([]);

  // Redirect if already logged in
  useEffect(() => {
    if (localStorage.getItem("qkard_businessId")) navigate("/dashboard", { replace: true });
  }, [navigate]);

  useEffect(() => {
    // Sign out Firebase silently on login page load
    auth.signOut().catch(() => { });
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // ── Step 1: Phone check ──────────────────────────────────────────
  async function handlePhoneNext() {
    setError(""); setInfo("");
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) { setError("Enter a valid 10-digit mobile number."); return; }
    setCheckingPhone(true);
    try {
      const res = await fetch(`${API}/auth/check-phone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: "+91" + digits }),
      });
      const data = await res.json();
      if (data.exists && data.hasPin) {
        // Existing user with PIN → PIN step
        setIsExistingUser(true);
        setHasPin(true);
        setStep("pin");
        setTimeout(() => pinRefs.current[0]?.focus(), 100);
      } else {
        // New user OR existing without PIN → OTP
        setIsExistingUser(data.exists);
        setHasPin(false);
        await sendOtp(digits);
      }
    } catch {
      // On network error fall back to OTP
      await sendOtp(phone.replace(/\D/g, ""));
    } finally {
      setCheckingPhone(false);
    }
  }

  // ── Send Firebase OTP ────────────────────────────────────────────
  async function sendOtp(digits) {
    try {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        auth, "recaptcha-container", { size: "invisible", callback: () => { } }
      );
      await recaptchaVerifierRef.current.render();
      const result = await signInWithPhoneNumber(auth, `+91${digits}`, recaptchaVerifierRef.current);
      setConfirmResult(result);
      setStep("otp");
      setResendCooldown(30);
    } catch (err) {
      console.error(err);
      setError("Failed to send OTP. Please try again.");
      recaptchaVerifierRef.current?.clear();
      recaptchaVerifierRef.current = null;
    }
  }

  // ── Step 2a: PIN verify ──────────────────────────────────────────
  async function handlePinVerify() {
    setError("");
    const code = pin.join("");
    if (code.length !== 4) { setError("Enter your 4-digit PIN."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/verify-pin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: "+91" + phone.replace(/\D/g, ""), pin: code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Invalid PIN");
      saveAndRedirect(data);
    } catch (err) {
      setError(err.message || "Incorrect PIN. Try again.");
      setPin(["", "", "", ""]);
      setTimeout(() => pinRefs.current[0]?.focus(), 100);
    } finally {
      setLoading(false);
    }
  }

  // ── Step 2b: OTP verify ──────────────────────────────────────────
  async function handleVerifyOtp() {
    setError("");
    const code = otp.join("");
    if (code.length !== 6) { setError("Enter the 6-digit OTP."); return; }
    setLoading(true);
    try {
      const cred = await confirmResult.confirm(code);
      const idToken = await cred.user.getIdToken();
      const res = await fetch(`${API}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Verification failed");

      if (data.isNewUser) {
        // New user — go to signup with phone pre-filled
        localStorage.setItem("qkard_signup_phone", "+91" + phone.replace(/\D/g, ""));
        localStorage.setItem("qkard_signup_token", idToken);
        navigate("/signup", { replace: true });
        return;
      }

      // Existing user verified via OTP
      if (!data.hasPin) {
        // No PIN set yet — ask them to set one now
        setIdTokenForPin(idToken);
        setStep("set-pin");
        setTimeout(() => newPinRefs.current[0]?.focus(), 100);
      } else {
        // Has PIN already (reset flow) — go straight to dashboard
        saveAndRedirect(data);
      }
    } catch (err) {
      console.error(err);
      setError("Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // ── Step 3: Set PIN (first time or reset) ───────────────────────
  async function handleSetPin() {
    setError("");
    const p1 = newPin.join("");
    const p2 = confirmPin.join("");
    if (p1.length !== 4) { setError("Enter a 4-digit PIN."); return; }
    if (p1 !== p2) {
      setError("PINs don't match. Try again.");
      setConfirmPin(["", "", "", ""]);
      setTimeout(() => confirmPinRefs.current[0]?.focus(), 100);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/set-pin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken: idTokenForPin, pin: p1 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to set PIN");

      // PIN set — now fetch business data and go to dashboard
      const verifyRes = await fetch(`${API}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken: idTokenForPin }),
      });
      const verifyData = await verifyRes.json();
      saveAndRedirect(verifyData);
    } catch (err) {
      setError(err.message || "Failed to set PIN.");
    } finally {
      setLoading(false);
    }
  }

  // ── Save to localStorage and redirect ───────────────────────────
  function saveAndRedirect(data) {
    localStorage.setItem("qkard_businessId", data.business.businessId);
    localStorage.setItem("qkard_businessName", data.business.businessName);
    localStorage.setItem("qkard_phone", data.phone);
    navigate("/dashboard", { replace: true });
  }

  // ── Forgot PIN → re-send OTP ─────────────────────────────────────
  async function handleForgotPin() {
    setError(""); setPin(["", "", "", ""]);
    await sendOtp(phone.replace(/\D/g, ""));
  }

  // ── Input helpers ────────────────────────────────────────────────
  const handlePinChange = (val, idx, arr, setArr, refs, onComplete) => {
    const v = val.replace(/\D/g, "").slice(-1);
    const next = [...arr]; next[idx] = v; setArr(next);
    if (v && idx < arr.length - 1) refs.current[idx + 1]?.focus();
    if (!v && idx > 0) refs.current[idx - 1]?.focus();
    if (onComplete && next.every(d => d) && next.join("").length === arr.length) onComplete(next.join(""));
  };

  const handleOtpChange = (val, idx) => {
    const v = val.replace(/\D/g, "").slice(-1);
    const next = [...otp]; next[idx] = v; setOtp(next);
    if (v && idx < 5) otpRefs.current[idx + 1]?.focus();
    if (!v && idx > 0) otpRefs.current[idx - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) { setOtp(pasted.split("")); otpRefs.current[5]?.focus(); }
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div style={S.root}>
      <div id="recaptcha-container" />

      <div style={S.logoWrap} onClick={() => navigate("/")}>
        <span style={S.logo}>QKARD</span>
      </div>

      <div style={S.card}>

        {/* ── PHONE ── */}
        {step === "phone" && (
          <>
            <div style={S.iconWrap}>📲</div>
            <h1 style={S.title}>Welcome to QKARD</h1>
            <p style={S.subtitle}>Enter your mobile number to continue</p>
            <div style={S.inputGroup}>
              <span style={S.prefix}>+91</span>
              <input
                style={S.input} type="tel" inputMode="numeric"
                maxLength={10} placeholder="9876543210" value={phone}
                onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handlePhoneNext()}
                autoFocus
              />
            </div>
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btnPrimary, opacity: checkingPhone ? 0.7 : 1 }}
              onClick={handlePhoneNext} disabled={checkingPhone}>
              {checkingPhone ? "Checking..." : "Continue →"}
            </button>
            <p style={S.switchText}>
              New to QKARD?{" "}
              <span style={S.link} onClick={() => navigate("/signup")}>Create free account</span>
            </p>
          </>
        )}

        {/* ── PIN ── */}
        {step === "pin" && (
          <>
            <div style={S.iconWrap}>🔒</div>
            <h1 style={S.title}>Enter your PIN</h1>
            <p style={S.subtitle}>
              Welcome back!<br />
              <span style={{ fontSize: 12, color: C.muted }}>+91 {phone}</span>
            </p>
            <div style={S.pinRow}>
              {pin.map((digit, i) => (
                <input key={i} ref={(el) => (pinRefs.current[i] = el)}
                  style={{ ...S.pinBox, borderColor: digit ? C.accent : C.border }}
                  type="password" inputMode="numeric" maxLength={1} value={digit}
                  onChange={(e) => handlePinChange(e.target.value, i, pin, setPin, pinRefs)}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !pin[i] && i > 0) pinRefs.current[i - 1]?.focus();
                    if (e.key === "Enter" && pin.join("").length === 4) handlePinVerify();
                  }}
                />
              ))}
            </div>
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1 }}
              onClick={handlePinVerify} disabled={loading}>
              {loading ? "Verifying..." : "Login →"}
            </button>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              <span style={S.link} onClick={() => { setStep("phone"); setPin(["", "", "", ""]); setError(""); }}>
                ← Change number
              </span>
              <span style={S.link} onClick={handleForgotPin}>Forgot PIN?</span>
            </div>
          </>
        )}

        {/* ── OTP ── */}
        {step === "otp" && (
          <>
            <div style={S.iconWrap}>💬</div>
            <h1 style={S.title}>{isExistingUser ? "Verify to reset PIN" : "Verify your number"}</h1>
            <p style={S.subtitle}>
              OTP sent to +91 {phone}<br />
              <span style={{ fontSize: 12, color: C.muted }}>Check your SMS</span>
            </p>
            <div style={S.otpRow} onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input key={i} ref={(el) => (otpRefs.current[i] = el)}
                  style={{ ...S.otpBox, borderColor: digit ? C.accent : C.border }}
                  type="text" inputMode="numeric" maxLength={1} value={digit}
                  onChange={(e) => handleOtpChange(e.target.value, i)}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
                    if (e.key === "Enter" && otp.join("").length === 6) handleVerifyOtp();
                  }}
                  autoFocus={i === 0}
                />
              ))}
            </div>
            {error && <div style={S.error}>{error}</div>}
            <button style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1 }}
              onClick={handleVerifyOtp} disabled={loading}>
              {loading ? "Verifying..." : "Verify OTP →"}
            </button>
            <p style={S.resendRow}>
              {resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : <span style={S.link} onClick={() => sendOtp(phone.replace(/\D/g, ""))}>Resend OTP</span>
              }
              {" · "}
              <span style={S.link} onClick={() => { setStep("phone"); setOtp(["", "", "", "", "", ""]); setError(""); }}>
                Change number
              </span>
            </p>
          </>
        )}

        {/* ── SET PIN ── */}
        {step === "set-pin" && (
          <>
            <div style={S.iconWrap}>🔐</div>
            <h1 style={S.title}>{hasPin ? "Reset your PIN" : "Set a PIN"}</h1>
            <p style={S.subtitle}>
              {hasPin ? "Set a new 4-digit PIN for quick login" : "Create a 4-digit PIN for faster future logins"}
            </p>

            <p style={{ ...S.label, marginTop: 8 }}>New PIN</p>
            <div style={S.pinRow}>
              {newPin.map((digit, i) => (
                <input key={i} ref={(el) => (newPinRefs.current[i] = el)}
                  style={{ ...S.pinBox, borderColor: digit ? C.accent : C.border }}
                  type="password" inputMode="numeric" maxLength={1} value={digit}
                  onChange={(e) => handlePinChange(e.target.value, i, newPin, setNewPin, newPinRefs,
                    () => setTimeout(() => confirmPinRefs.current[0]?.focus(), 100)
                  )}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !newPin[i] && i > 0) newPinRefs.current[i - 1]?.focus();
                  }}
                  autoFocus={i === 0}
                />
              ))}
            </div>

            <p style={{ ...S.label, marginTop: 16 }}>Confirm PIN</p>
            <div style={S.pinRow}>
              {confirmPin.map((digit, i) => (
                <input key={i} ref={(el) => (confirmPinRefs.current[i] = el)}
                  style={{ ...S.pinBox, borderColor: digit ? C.accent : C.border }}
                  type="password" inputMode="numeric" maxLength={1} value={digit}
                  onChange={(e) => handlePinChange(e.target.value, i, confirmPin, setConfirmPin, confirmPinRefs)}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !confirmPin[i] && i > 0) confirmPinRefs.current[i - 1]?.focus();
                    if (e.key === "Enter" && confirmPin.join("").length === 4) handleSetPin();
                  }}
                />
              ))}
            </div>

            {error && <div style={S.error}>{error}</div>}
            {info && <div style={{ ...S.error, background: "#4caf5018", borderColor: "#4caf5044", color: C.success }}>{info}</div>}

            <button style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1, marginTop: 20 }}
              onClick={handleSetPin} disabled={loading}>
              {loading ? "Saving..." : "Save PIN & Login →"}
            </button>
          </>
        )}

      </div>

      <p style={S.footNote}>
        By continuing you agree to our{" "}
        <span style={S.footLink}>Terms</span> &{" "}
        <span style={S.footLink}>Privacy Policy</span>
      </p>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────
const S = {
  root: {
    minHeight: "100vh", background: C.bg, display: "flex",
    flexDirection: "column", alignItems: "center", justifyContent: "center",
    padding: "20px 16px", fontFamily: "'DM Sans', sans-serif",
  },
  logoWrap: { marginBottom: 28, cursor: "pointer" },
  logo: {
    fontSize: 26, fontWeight: 800, letterSpacing: "0.12em",
    color: C.accent, fontFamily: "'DM Mono', monospace",
  },
  card: {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 16,
    padding: "36px 32px", width: "100%", maxWidth: 400,
  },
  iconWrap: { fontSize: 36, marginBottom: 14, textAlign: "center" },
  title: { fontSize: 22, fontWeight: 700, color: C.text, margin: "0 0 8px", textAlign: "center" },
  subtitle: { fontSize: 14, color: C.muted, margin: "0 0 24px", textAlign: "center", lineHeight: 1.6 },
  label: { fontSize: 13, color: C.muted, marginBottom: 10, fontWeight: 500 },
  inputGroup: {
    display: "flex", alignItems: "center", border: `1px solid ${C.border}`,
    borderRadius: 10, overflow: "hidden", marginBottom: 16, background: "#1a1a24",
  },
  prefix: { padding: "13px 12px", color: C.muted, fontSize: 15, borderRight: `1px solid ${C.border}` },
  input: {
    flex: 1, background: "transparent", border: "none", outline: "none",
    color: C.text, fontSize: 16, padding: "13px 14px",
    fontFamily: "'DM Mono', monospace",
  },
  pinRow: { display: "flex", gap: 12, justifyContent: "center", marginBottom: 20 },
  pinBox: {
    width: 52, height: 58, textAlign: "center", fontSize: 22, fontWeight: 700,
    background: "#1a1a24", border: `2px solid ${C.border}`, borderRadius: 10,
    color: C.text, outline: "none", fontFamily: "'DM Mono', monospace",
    transition: "border-color 0.15s",
  },
  otpRow: { display: "flex", gap: 8, justifyContent: "center", marginBottom: 20 },
  otpBox: {
    width: 44, height: 52, textAlign: "center", fontSize: 20, fontWeight: 700,
    background: "#1a1a24", border: `2px solid ${C.border}`, borderRadius: 10,
    color: C.text, outline: "none", fontFamily: "'DM Mono', monospace",
    transition: "border-color 0.15s",
  },
  btnPrimary: {
    width: "100%", background: C.accent, color: "#0a0a0f", border: "none",
    padding: "13px 0", borderRadius: 10, fontSize: 15, fontWeight: 700,
    cursor: "pointer", marginBottom: 16, letterSpacing: "0.01em",
  },
  error: {
    background: "#ff5c5c18", border: "1px solid #ff5c5c44", color: C.error,
    borderRadius: 8, padding: "9px 14px", fontSize: 13, marginBottom: 12,
  },
  switchText: { fontSize: 14, color: C.muted, margin: 0, textAlign: "center" },
  link: { color: C.accent, cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 },
  resendRow: { fontSize: 13, color: C.muted, margin: 0, textAlign: "center" },
  footNote: { marginTop: 28, fontSize: 12, color: C.muted },
  footLink: { color: C.muted, textDecoration: "underline", cursor: "pointer" },
};